import { useState, useEffect, useMemo, useCallback } from 'react';
import { getRaces, getDistricts, seedRaces } from '../lib/api';
import { groupRacesByMonth, MONTH_NAMES_PT } from '../lib/utils';
import { HeroSection } from '../components/HeroSection';
import { FilterPills } from '../components/FilterPills';
import { MonthHeader } from '../components/MonthHeader';
import { RaceCard } from '../components/RaceCard';
import { EmptyState } from '../components/EmptyState';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { RaceDetailModal } from '../components/RaceDetailModal';
import { Button } from '../components/ui/button';
import { RefreshCw } from 'lucide-react';

export const CalendarPage = ({ isInternational }) => {
  const [races, setRaces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [activeType, setActiveType] = useState(null);
  const [activeMonth, setActiveMonth] = useState(null);
  const [activeDistrict, setActiveDistrict] = useState(null);
  
  // Modal
  const [selectedRace, setSelectedRace] = useState(null);

  const fetchRaces = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        is_international: isInternational,
      };
      
      if (activeType) params.race_type = activeType;
      if (activeMonth) params.month = activeMonth;
      if (activeDistrict) params.district = activeDistrict;
      if (searchQuery) params.search = searchQuery;
      
      const data = await getRaces(params);
      setRaces(data);
    } catch (err) {
      console.error('Error fetching races:', err);
      setError('Erro ao carregar provas');
    } finally {
      setLoading(false);
    }
  }, [isInternational, activeType, activeMonth, activeDistrict, searchQuery]);

  const fetchDistricts = useCallback(async () => {
    try {
      const data = await getDistricts();
      setDistricts(data);
    } catch (err) {
      console.error('Error fetching districts:', err);
    }
  }, []);

  const handleSeedRaces = async () => {
    try {
      setSeeding(true);
      await seedRaces();
      await fetchRaces();
    } catch (err) {
      console.error('Error seeding races:', err);
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    fetchDistricts();
  }, [fetchDistricts]);

  useEffect(() => {
    fetchRaces();
  }, [fetchRaces]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== '') {
        fetchRaces();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchRaces]);

  // Group races by month
  const groupedRaces = useMemo(() => {
    return groupRacesByMonth(races);
  }, [races]);

  const resetFilters = () => {
    setSearchQuery('');
    setActiveType(null);
    setActiveMonth(null);
    setActiveDistrict(null);
  };

  const hasActiveFilters = searchQuery || activeType || activeMonth || activeDistrict;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="calendar-page">
      {/* Hero */}
      <HeroSection searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      
      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        {/* International badge */}
        {isInternational && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <h2 
              className="text-xl font-bold text-red-700 dark:text-red-400 uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Provas Internacionais
            </h2>
            <p className="text-sm text-red-600 dark:text-red-500">
              Provas fora de Portugal para planeares a tua escapadinha!
            </p>
          </div>
        )}
        
        {/* Filters */}
        <div className="mb-8">
          <FilterPills
            activeType={activeType}
            setActiveType={setActiveType}
            activeMonth={activeMonth}
            setActiveMonth={setActiveMonth}
            activeDistrict={activeDistrict}
            setActiveDistrict={setActiveDistrict}
            districts={districts}
          />
        </div>

        {/* Loading */}
        {loading && <LoadingSkeleton count={5} />}

        {/* Error */}
        {error && !loading && (
          <div className="text-center py-12">
            <p className="text-red-500 mb-4">{error}</p>
            <Button onClick={fetchRaces} variant="outline">
              Tentar novamente
            </Button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && races.length === 0 && (
          <div>
            <EmptyState 
              title={hasActiveFilters ? "Nenhuma prova encontrada" : "Ainda não há provas"}
              description={hasActiveFilters 
                ? "Tenta ajustar os filtros ou pesquisa" 
                : "Carrega dados de exemplo para começar"}
              showReset={hasActiveFilters}
              onReset={resetFilters}
            />
            {!hasActiveFilters && (
              <div className="text-center mt-4">
                <Button 
                  onClick={handleSeedRaces}
                  disabled={seeding}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="seed-races-btn"
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${seeding ? 'animate-spin' : ''}`} />
                  {seeding ? 'A carregar...' : 'Carregar provas de exemplo'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Race list grouped by month */}
        {!loading && !error && races.length > 0 && (
          <div className="space-y-2">
            {Object.entries(groupedRaces)
              .sort(([a], [b]) => parseInt(a) - parseInt(b))
              .map(([month, monthRaces]) => (
                <div key={month}>
                  <MonthHeader month={parseInt(month)} />
                  <div className="space-y-3 py-4">
                    {monthRaces.map((race, index) => (
                      <RaceCard 
                        key={race.id} 
                        race={race} 
                        index={index}
                        onClick={() => setSelectedRace(race)}
                      />
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Results count */}
        {!loading && races.length > 0 && (
          <div className="mt-8 text-center text-sm text-slate-500 dark:text-slate-400">
            {races.length} {races.length === 1 ? 'prova encontrada' : 'provas encontradas'}
          </div>
        )}
      </main>

      {/* Race Detail Modal */}
      {selectedRace && (
        <RaceDetailModal 
          race={selectedRace} 
          onClose={() => setSelectedRace(null)} 
        />
      )}
    </div>
  );
};

export default CalendarPage;
